import { cloneDeep } from "lib/lodash";
import { getAnimationData } from "lib/animatedElementUtils";
import { generateCssAnimationArray } from "lib/animationMigration";

// Sibling-side mixin used by the cssAnimationChange propagation handler. Writes
// the normalized cssAnimation array onto an element and rebuilds the owning
// page's animatedElements map so the sibling's timeline/player/export pipeline
// reads a cache consistent with the new animation stack.
//
// Mirrors the reconciliation performed by ActionBarOps.handleCssAnimationUpdate
// on the source design during user edits; the two paths must stay in sync so
// source and siblings share the same derived state.

type AnimationEntry = {
  name?: string;
  delay?: number;
  [key: string]: unknown;
};

type AnimationDataEntry = {
  animationDataKey: string;
  [key: string]: unknown;
};

type CssAnimationInput = AnimationEntry[] | AnimationEntry | null | undefined;

type ApplyCssAnimationArgs = {
  elementId: string;
  cssAnimation: CssAnimationInput;
};

type DesignLike = {
  elements: Record<string, AnimationEntry & { uniqueId?: string }>;
  pages: Record<string, { animatedElements: Record<string, unknown> }>;
  version: number;
  constructor: new (...args: unknown[]) => DesignLike;
  getElement: (elementId: string) => AnimationEntry | null | undefined;
  getElementPageId: (elementId: string) => string | undefined;
};

const DesignAnimationOps = {
  applyCssAnimation(
    this: DesignLike,
    { elementId, cssAnimation }: ApplyCssAnimationArgs
  ) {
    const element = this.getElement(elementId);
    if (!element) return this;

    // Spread order matters: the passed `cssAnimation` must win over the
    // element's stale value so generateCssAnimationArray normalizes the new
    // legacy object, not the pre-update snapshot.
    const normalized: AnimationEntry[] = Array.isArray(cssAnimation)
      ? cssAnimation
      : cssAnimation
        ? generateCssAnimationArray({ ...element, cssAnimation })
        : [];

    const pageId = this.getElementPageId(elementId);
    if (!pageId) return this;

    const page = cloneDeep(this.pages[pageId]);
    if (!page) return this;
    if (!page.animatedElements) page.animatedElements = {};

    const previousAnimationData: AnimationDataEntry[] = getAnimationData({
      ...element,
      pageId
    });
    previousAnimationData.forEach(({ animationDataKey }) => {
      delete page.animatedElements[animationDataKey];
    });

    const updatedElement = { ...element, cssAnimation: normalized };

    // Match ActionBarOps.handleCssAnimationUpdate: insert animatedElements for
    // any non-empty cssAnimation array (including `[{ name: "none" }]`
    // placeholders). Filtering "none" only on the sibling would diverge the
    // source and sibling animatedElements caches.
    if (normalized.length > 0) {
      const nextAnimationData: AnimationDataEntry[] = getAnimationData({
        ...updatedElement,
        pageId
      });
      nextAnimationData.forEach((entry: AnimationDataEntry) => {
        page.animatedElements[entry.animationDataKey] = entry;
      });
    }

    return new this.constructor({
      ...this,
      elements: {
        ...this.elements,
        [elementId]: updatedElement
      },
      pages: {
        ...this.pages,
        [pageId]: page
      },
      version: this.version + 1
    });
  }
};

export default DesignAnimationOps;
